import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Modal, Button, Checkbox, Icon, Select, Spinner, Text, TextField, Banner } from '@shopify/polaris';
import { MagicIcon, CheckCircleIcon, AlertTriangleIcon, InfoIcon } from '@shopify/polaris-icons';
import { formatToCurrency } from '../../../../shared/components/functions.jsx';

/*
  "Text to quotation": paste a customer request, an email, meeting notes, a specification or an old quotation, and the
  AI writes a free-form quotation from it: sections and groups with their own titles, the lines that support them, the
  PDF introduction and the terms the text states. Products are only linked when the text gives a part number; prices
  come from the text (or an earlier quotation with the same line). The server runs it as a background job (POST start,
  GET poll); the proposal is reviewed and edited here, and only "Create lines" writes to the quotation.
*/

const POLL_MS = 1500;
const MIN_TEXT = 20;
const MAX_TEXT = 60000;
const MIN_MARKUP = 1.54; // same rule as AddProductModal
const PRICE_GAP_FACTOR = 3;
const compactMoney = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

const LANGUAGE_OPTIONS = [
  { value: 'E', label: 'English' },
  { value: 'N', label: 'Nederlands' },
  { value: 'F', label: 'Français' }
];

const STEPS = [
  { id: 'extract', label: 'Writing the lines and the introduction' },
  { id: 'match', label: 'Looking up part numbers and earlier prices' },
  { id: 'assemble', label: 'Putting the proposal together' }
];

const SOURCE_LABELS = {
  text: 'From the text',
  markup: 'Cost × 1.54',
  catalog: 'Catalog',
  history: 'Earlier quotation',
  manual: 'Edited',
  none: 'No price'
};

const TERM_FIELDS = [
  { key: 'deliveryTime', label: 'Delivery time' },
  { key: 'warranty', label: 'Warranty' },
  { key: 'validity', label: 'Quotation validity' },
  { key: 'paymentLines', label: 'Payment' }
];

const STYLES = `
.tq-root { display: flex; flex-direction: column; gap: 14px; }
.tq-hint { color: #667085; font-size: 12.5px; line-height: 1.5; }
.tq-options { display: grid; grid-template-columns: 180px 1fr; gap: 12px; align-items: end; }
.tq-progress { padding: 36px 16px; display: flex; flex-direction: column; align-items: center; gap: 16px; }
.tq-steps { display: flex; flex-direction: column; gap: 10px; min-width: min(420px, 90%); }
.tq-step { display: flex; align-items: center; gap: 10px; font-size: 14px; color: #98a2b3; }
.tq-step.is-active { color: #101828; font-weight: 600; }
.tq-step.is-done { color: #027a48; }
.tq-dot { width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; flex: 0 0 auto; }
.tq-dot-idle { width: 8px; height: 8px; border-radius: 999px; background: #d0d5dd; }
.tq-summary { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; justify-content: space-between; padding: 10px 12px; background: #f9fafb; border: 1px solid #eaecf0; border-radius: 10px; }
.tq-stats { display: flex; flex-wrap: wrap; gap: 6px 14px; align-items: baseline; font-size: 13px; color: #475467; }
.tq-stats strong { color: #101828; font-size: 15px; }
.tq-chip { font-size: 12px; border-radius: 999px; padding: 1px 9px; background: #eaecf0; color: #344054; white-space: nowrap; }
.tq-chip.is-warn { background: #fef0c7; color: #93370d; }
.tq-chip.is-bad { background: #fee4e2; color: #b42318; }
.tq-cards { display: grid; grid-template-columns: minmax(0, 3fr) minmax(0, 2fr); gap: 12px; align-items: start; }
.tq-card { border: 1px solid #eaecf0; border-radius: 10px; padding: 10px 12px; background: #fff; display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.tq-card.is-off { background: #f9fafb; }
.tq-card-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.tq-textarea { width: 100%; box-sizing: border-box; font: inherit; font-size: 13px; line-height: 1.55; padding: 8px 10px; border: 1px solid #d0d5dd; border-radius: 8px; resize: vertical; min-height: 180px; color: #101828; }
.tq-textarea:focus { outline: none; border-color: #7a5af8; box-shadow: 0 0 0 2px rgba(122,90,248,.15); }
.tq-textarea:disabled { background: #f2f4f7; color: #667085; }
.tq-term { display: grid; grid-template-columns: 24px 120px minmax(0, 1fr); gap: 6px; align-items: start; font-size: 13px; }
.tq-term-label { color: #475467; padding-top: 5px; }
.tq-notes { border: 1px solid #d1e9ff; background: #f5faff; border-radius: 10px; padding: 10px 12px; font-size: 13px; color: #344054; }
.tq-notes ul { margin: 6px 0 0; padding-left: 18px; }
.tq-notes li { margin: 2px 0; }
.tq-table-wrap { border: 1px solid #eaecf0; border-radius: 10px; overflow: auto; max-height: min(62vh, 760px); }
.tq-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 13px; }
.tq-table th { position: sticky; top: 0; z-index: 2; background: #f9fafb; text-align: left; font-weight: 600; font-size: 12px; color: #667085; padding: 7px 8px; border-bottom: 1px solid #eaecf0; white-space: nowrap; }
.tq-table td { padding: 5px 8px; border-bottom: 1px solid #f2f4f7; vertical-align: top; }
.tq-table .num { text-align: right; }
.tq-section td { background: #ebe9fe; border-bottom: 1px solid #d9d6fe; padding-top: 8px; padding-bottom: 8px; }
.tq-group td { background: #f4f3ff; border-bottom: 1px solid #e4e1fd; padding-top: 6px; padding-bottom: 6px; }
.tq-head { display: flex; align-items: center; gap: 8px; min-width: 0; }
.tq-code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; color: #5925dc; white-space: nowrap; }
.tq-moved { font-size: 11.5px; color: #93370d; white-space: nowrap; }
.tq-input { width: 100%; box-sizing: border-box; font: inherit; font-size: 13px; padding: 4px 6px; border: 1px solid transparent; border-radius: 6px; background: transparent; color: #101828; }
.tq-input:hover { border-color: #d0d5dd; background: #fff; }
.tq-input:focus { outline: none; border-color: #7a5af8; background: #fff; box-shadow: 0 0 0 2px rgba(122,90,248,.15); }
.tq-input.num { text-align: right; }
.tq-input.is-bad { border-color: #fda29b; background: #fffbfa; }
.tq-input.is-warn { border-color: #fec84b; background: #fffcf5; }
.tq-input.is-boxed { border-color: #eaecf0; background: #fff; }
.tq-label-input { font-weight: 600; }
.tq-section-input { font-weight: 700; font-size: 14px; }
.tq-select { width: 100%; box-sizing: border-box; font: inherit; font-size: 12.5px; padding: 4px 6px; border: 1px solid #eaecf0; border-radius: 6px; background: #fff; color: #344054; max-width: 230px; }
.tq-select.is-source { border-radius: 999px; padding: 2px 8px; font-size: 12px; background: #f2f4f7; border-color: transparent; max-width: 170px; }
.tq-select.is-source.src-none { background: #fee4e2; color: #b42318; }
.tq-select.is-source.src-text, .tq-select.is-source.src-markup { background: #d1fadf; color: #05603a; }
.tq-select.is-source.src-catalog { background: #d1e9ff; color: #175cd3; }
.tq-select.is-source.src-history { background: #ebe9fe; color: #5925dc; }
.tq-row.is-excluded td { opacity: .45; }
.tq-sub { display: flex; flex-wrap: wrap; gap: 4px 10px; margin: 1px 6px 0; font-size: 11.5px; color: #667085; }
.tq-link { background: none; border: none; padding: 0; font-size: 11.5px; color: #5925dc; cursor: pointer; }
.tq-link:hover { text-decoration: underline; }
.tq-warn { color: #93370d; }
.tq-bad { color: #b42318; }
.tq-details { margin: 4px 6px 2px; padding-left: 16px; font-size: 12px; color: #475467; }
.tq-total { white-space: nowrap; font-variant-numeric: tabular-nums; }
.tq-empty { padding: 40px 16px; display: flex; flex-direction: column; align-items: center; gap: 8px; text-align: center; }
@media (max-width: 900px) { .tq-cards { grid-template-columns: 1fr; } }
`;

async function requestJson(url, options) {
  const response = await fetch(url, options);
  let body = null;
  try {
    body = await response.json();
  } catch (err) {
    body = null;
  }
  if (!response.ok) throw new Error(body?.message || `Request failed (${response.status})`);
  return body;
}

function toNumber(value) {
  if (value == null || value === '') return 0;
  const number = Number(String(value).replace(',', '.'));
  return Number.isFinite(number) ? number : 0;
}

function round2(value) {
  return Math.round(value * 100) / 100;
}

function lineTotal(item) {
  return toNumber(item.unitSellingPrice) * toNumber(item.quantity) * (1 - toNumber(item.discountPercent) / 100);
}

// Same rules as itemWarnings in server/services/quotationFromText.js
function warningsFor(item) {
  const warnings = [];
  const ap = toNumber(item.unitPurchasePrice);
  const vp = toNumber(item.unitSellingPrice);
  if (vp <= 0 && item.priceSource !== 'text') warnings.push('noPrice');
  if (ap > 0 && vp < round2(ap * MIN_MARKUP) - 0.005) warnings.push('lowMargin');
  const product = item.productId ? item.candidates.find((candidate) => candidate.productId === item.productId) : null;
  const ref = item.priceRef ? item.priceRefs.find((entry) => entry.ref === item.priceRef) : null;
  if (product?.vp > 0 && ref?.vp > 0 && Math.max(product.vp, ref.vp) / Math.min(product.vp, ref.vp) > PRICE_GAP_FACTOR) warnings.push('priceGap');
  if (item.priceSource === 'history' && ref && Number(ref.quantity) === 1 && toNumber(item.quantity) >= 5) warnings.push('unitCheck');
  return warnings;
}

function prepareSections(sections) {
  return sections.map((section, sectionIndex) => ({
    ...section,
    key: `s${sectionIndex}`,
    groups: section.groups.map((group, groupIndex) => ({
      ...group,
      key: `s${sectionIndex}g${groupIndex}`,
      items: group.items.map((item) => ({
        ...item,
        include: true,
        showDetails: false,
        quantity: String(item.quantity),
        unitPurchasePrice: String(item.unitPurchasePrice),
        unitSellingPrice: String(item.unitSellingPrice),
        original: {
          unitPurchasePrice: String(item.unitPurchasePrice),
          unitSellingPrice: String(item.unitSellingPrice),
          priceSource: item.priceSource,
          priceRef: item.priceRef,
          productId: item.productId
        }
      }))
    }))
  }));
}

// The stated terms as editable rows, each with its own checkbox (payment lines edited as one line per step)
function prepareTerms(terms) {
  if (!terms) return [];
  return TERM_FIELDS.filter((field) => (field.key === 'paymentLines' ? terms.paymentLines?.length : terms[field.key])).map((field) => ({
    ...field,
    use: true,
    value: field.key === 'paymentLines' ? terms.paymentLines.join('\n') : terms[field.key]
  }));
}

function candidateLabel(candidate) {
  const name = [candidate.ref, candidate.name].filter(Boolean).join(' · ');
  return `${name}${candidate.vp > 0 ? ` · ${formatToCurrency(candidate.vp)}` : ''}`;
}

function refLabel(ref) {
  const where = [`qty ${ref.quantity}`, ref.projectNumber ? `project ${ref.projectNumber}` : null, ref.date ? ref.date.slice(0, 7) : null].filter(Boolean).join(', ');
  return `${ref.description} · ${formatToCurrency(ref.vp)}${where ? ` (${where})` : ''}`;
}

function ItemRow({ item, showProduct, onChange, onToggle }) {
  const warnings = warningsFor(item);
  const product = item.productId ? item.candidates.find((candidate) => candidate.productId === item.productId) : null;
  const ref = item.priceRef ? item.priceRefs.find((entry) => entry.ref === item.priceRef) : null;
  const minPrice = round2(toNumber(item.unitPurchasePrice) * MIN_MARKUP);
  const heldBack = item.heldBackRef && item.priceSource === 'none' ? item.priceRefs.find((entry) => entry.ref === item.heldBackRef) : null;

  const setProduct = (value) => {
    const productId = value ? Number(value) : null;
    const next = item.candidates.find((candidate) => candidate.productId === productId);
    const patch = { productId };
    // a price that came from the text or the user stays; a looked-up price follows the product
    if (next && next.vp > 0 && !['text', 'markup', 'manual'].includes(item.priceSource)) {
      Object.assign(patch, { unitPurchasePrice: String(next.ap), unitSellingPrice: String(next.vp), priceSource: 'catalog', priceRef: null });
    } else if (!next && item.priceSource === 'catalog') {
      patch.priceSource = 'manual'; // the catalog price stays, but no product backs it any more
    }
    onChange(patch);
  };

  const setSource = (value) => {
    if (value === 'original') {
      onChange({ unitPurchasePrice: item.original.unitPurchasePrice, unitSellingPrice: item.original.unitSellingPrice, priceSource: item.original.priceSource, priceRef: item.original.priceRef });
    } else if (value === 'catalog' && product) {
      onChange({ unitPurchasePrice: String(product.ap), unitSellingPrice: String(product.vp), priceSource: 'catalog', priceRef: null });
    } else {
      const picked = item.priceRefs.find((entry) => entry.ref === value);
      if (picked) onChange({ unitPurchasePrice: String(picked.ap), unitSellingPrice: String(picked.vp), priceSource: 'history', priceRef: picked.ref });
    }
  };

  const isOriginal =
    item.priceSource === item.original.priceSource &&
    item.unitSellingPrice === item.original.unitSellingPrice &&
    item.unitPurchasePrice === item.original.unitPurchasePrice &&
    (item.priceSource !== 'history' || item.priceRef === item.original.priceRef);
  const backToOriginalCatalog = item.priceSource === 'catalog' && item.original.priceSource === 'catalog' && item.original.productId === item.productId;
  const sourceValue = isOriginal || backToOriginalCatalog ? 'original' : item.priceSource === 'history' ? item.priceRef : item.priceSource;
  const sourceTitle =
    item.priceSource === 'history' && ref
      ? `Price of an earlier quotation line: ${refLabel(ref)}`
      : item.priceSource === 'catalog' && product
      ? `Catalog price of ${candidateLabel(product)}`
      : item.priceSource === 'markup'
      ? 'The text gives a cost price; selling price = cost × 1.54'
      : item.priceSource === 'text'
      ? 'Price written in the text'
      : item.priceSource === 'none'
      ? item.priceRefs.length
        ? 'No price in the text: fill it in, or pick a similar line from an earlier quotation'
        : 'No price in the text: fill it in'
      : 'Edited by you';

  return (
    <tr className={`tq-row${item.include ? '' : ' is-excluded'}`}>
      <td style={{ width: 28 }}>
        <Checkbox label="Include this line" labelHidden checked={item.include} onChange={onToggle} />
      </td>
      <td className="tq-code" style={{ paddingTop: 9, textAlign: 'right' }}>
        {item.unitNumber}
      </td>
      <td style={{ minWidth: 260 }}>
        <input
          className="tq-input"
          value={item.description}
          maxLength={500}
          title={item.source ? `From the text: “${item.source}”` : undefined}
          onChange={(event) => onChange({ description: event.target.value })}
          aria-label="Description"
        />
        {(item.details.length > 0 || warnings.length > 0 || heldBack) && (
          <div className="tq-sub">
            {item.details.length > 0 && (
              <button type="button" className="tq-link" onClick={() => onChange({ showDetails: !item.showDetails })}>
                {`${item.showDetails ? '▾' : '▸'} ${item.details.length} technical detail${item.details.length === 1 ? '' : 's'}`}
              </button>
            )}
            {warnings.includes('noPrice') && <span className="tq-bad">Needs a price</span>}
            {heldBack && (
              <span className="tq-warn" title={refLabel(heldBack)}>
                {`An earlier quotation has ${formatToCurrency(heldBack.vp)} for quantity 1 (a lump sum?): `}
                <button
                  type="button"
                  className="tq-link"
                  onClick={() => onChange({ unitPurchasePrice: String(heldBack.ap), unitSellingPrice: String(heldBack.vp), priceSource: 'history', priceRef: heldBack.ref })}
                >
                  use it per unit
                </button>
              </span>
            )}
            {warnings.includes('lowMargin') && (
              <span className="tq-warn">
                {`Under the 54 % markup (min ${formatToCurrency(minPrice)}) `}
                <button type="button" className="tq-link" onClick={() => onChange({ unitSellingPrice: String(minPrice), priceSource: 'manual' })}>
                  Set to minimum
                </button>
              </span>
            )}
            {warnings.includes('priceGap') && <span className="tq-warn">{`Catalog price differs a lot from the earlier quotation (${formatToCurrency(ref?.vp || 0)})`}</span>}
            {warnings.includes('unitCheck') && <span className="tq-warn">The earlier price was for quantity 1 (a lump sum?): check the unit</span>}
          </div>
        )}
        {item.showDetails && (
          <ul className="tq-details">
            {item.details.map((detail, index) => (
              <li key={index}>{detail}</li>
            ))}
          </ul>
        )}
      </td>
      {showProduct && (
        <td style={{ width: 210 }}>
          {item.candidates.length > 0 ? (
            <select
              className="tq-select"
              value={item.productId || ''}
              onChange={(event) => setProduct(event.target.value)}
              aria-label="Catalog product"
              title={product ? candidateLabel(product) : `Part number ${item.partNumber || ''}: not linked`}
            >
              <option value="">Free text line</option>
              {item.candidates.map((candidate) => (
                <option key={candidate.productId} value={candidate.productId}>
                  {`#${candidate.productId} ${candidateLabel(candidate)}`}
                </option>
              ))}
            </select>
          ) : null}
        </td>
      )}
      <td style={{ width: 70 }}>
        <input className="tq-input num" inputMode="decimal" value={item.quantity} onChange={(event) => onChange({ quantity: event.target.value })} aria-label="Quantity" />
      </td>
      <td style={{ width: 96 }}>
        <input
          className="tq-input num"
          inputMode="decimal"
          value={item.unitPurchasePrice}
          onChange={(event) => onChange({ unitPurchasePrice: event.target.value, priceSource: 'manual' })}
          aria-label="Cost price (AP)"
        />
      </td>
      <td style={{ width: 104 }}>
        <input
          className={`tq-input num${warnings.includes('noPrice') ? ' is-bad' : warnings.some((warning) => warning !== 'noPrice') ? ' is-warn' : ''}`}
          inputMode="decimal"
          value={item.unitSellingPrice}
          onChange={(event) => onChange({ unitSellingPrice: event.target.value, priceSource: 'manual' })}
          aria-label="Selling price (VP)"
        />
      </td>
      <td className="num tq-total" style={{ paddingTop: 9 }}>
        {formatToCurrency(lineTotal(item))}
      </td>
      <td style={{ width: 170 }}>
        <select className={`tq-select is-source src-${item.priceSource}`} value={sourceValue} onChange={(event) => setSource(event.target.value)} title={sourceTitle} aria-label="Price source">
          <option value="original">{`${SOURCE_LABELS[item.original.priceSource]}${
            item.original.priceSource !== 'none' ? ` · ${compactMoney.format(toNumber(item.original.unitSellingPrice))}` : ''
          }`}</option>
          {item.priceSource === 'manual' && <option value="manual">{SOURCE_LABELS.manual}</option>}
          {product && product.vp > 0 && !(item.original.priceSource === 'catalog' && item.original.productId === product.productId) && (
            <option value="catalog">{`Catalog · ${compactMoney.format(product.vp)}`}</option>
          )}
          {item.priceRefs.map((entry) => (
            <option key={entry.ref} value={entry.ref}>
              {`Earlier: ${refLabel(entry)}`}
            </option>
          ))}
        </select>
      </td>
      <td style={{ width: 44, textAlign: 'center' }}>
        <Checkbox label="Optional" labelHidden checked={item.isOptional} onChange={(value) => onChange({ isOptional: value })} />
      </td>
    </tr>
  );
}

export default function TextToQuotationModal({ open, onClose, quotationId, pdfLanguage = 'E', existingLineCount = 0, hasIntroduction = false, onApplied }) {
  const [text, setText] = useState('');
  const [instructions, setInstructions] = useState('');
  const [language, setLanguage] = useState(pdfLanguage || 'E');
  const [replace, setReplace] = useState(false);
  const [phase, setPhase] = useState('input'); // input | running | review | applying | applied
  const [progress, setProgress] = useState({ step: 'prepare', done: 0, total: 0 });
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [sections, setSections] = useState([]);
  const [introduction, setIntroduction] = useState({ text: '', use: false });
  const [terms, setTerms] = useState([]);
  const [runMode, setRunMode] = useState('append');
  const [error, setError] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const runRef = useRef(0);

  const resetProposal = () => {
    setResult(null);
    setSections([]);
    setIntroduction({ text: '', use: false });
    setTerms([]);
    setOutcome(null);
    setError(null);
  };

  // A new quotation version starts clean; closing the modal keeps everything (and a running job keeps running)
  useEffect(() => {
    runRef.current += 1;
    setText('');
    setInstructions('');
    setReplace(false);
    setPhase('input');
    resetProposal();
  }, [quotationId]);

  useEffect(() => {
    if (phase === 'input') setLanguage(pdfLanguage || 'E');
  }, [pdfLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (phase !== 'running') return undefined;
    const startedAt = Date.now();
    setElapsed(0);
    const timer = setInterval(() => setElapsed(Math.round((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [phase]);

  const start = useCallback(async () => {
    const runId = ++runRef.current;
    const mode = replace && existingLineCount > 0 ? 'replace' : 'append';
    setError(null);
    setOutcome(null);
    setRunMode(mode);
    setProgress({ step: 'prepare', done: 0, total: 0 });
    setPhase('running');
    try {
      let job = await requestJson(`/api/project-quotations/${quotationId}/text-import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, instructions, language, mode })
      });
      while (job.status === 'running') {
        await new Promise((resolve) => setTimeout(resolve, POLL_MS));
        if (runRef.current !== runId) return;
        job = await requestJson(`/api/project-quotations/${quotationId}/text-import/${job.jobId}?_ts=${Date.now()}`);
        if (runRef.current !== runId) return;
        setProgress(job.progress || { step: 'extract', done: 0, total: 0 });
      }
      if (runRef.current !== runId) return;
      if (job.status === 'error') throw new Error(job.error || 'The AI request failed');
      setResult(job.result);
      setSections(prepareSections(job.result.sections || []));
      // the introduction replaces an existing one only when asked (or when everything is replaced anyway)
      setIntroduction({ text: job.result.introduction || '', use: !!job.result.introduction && (!hasIntroduction || mode === 'replace') });
      setTerms(prepareTerms(job.result.terms));
      setPhase('review');
    } catch (err) {
      if (runRef.current !== runId) return;
      setError(err.message);
      setPhase('input');
    }
  }, [quotationId, text, instructions, language, replace, existingLineCount, hasIntroduction]);

  const updateSection = (sectionKey, update) => setSections((prev) => prev.map((section) => (section.key === sectionKey ? update(section) : section)));
  const updateGroup = (sectionKey, groupKey, update) =>
    updateSection(sectionKey, (section) => ({ ...section, groups: section.groups.map((group) => (group.key === groupKey ? update(group) : group)) }));
  const updateItem = (sectionKey, groupKey, itemId, patch) =>
    updateGroup(sectionKey, groupKey, (group) => ({ ...group, items: group.items.map((item) => (item.id === itemId ? { ...item, ...patch } : item)) }));
  const setIncluded = (include, sectionKey = null, groupKey = null) =>
    setSections((prev) =>
      prev.map((section) =>
        sectionKey && section.key !== sectionKey
          ? section
          : { ...section, groups: section.groups.map((group) => (groupKey && group.key !== groupKey ? group : { ...group, items: group.items.map((item) => ({ ...item, include })) })) }
      )
    );

  const summary = useMemo(() => {
    const stats = { lines: 0, groups: 0, total: 0, optional: 0, cost: 0, noPrice: 0, lowMargin: 0, check: 0, products: 0, all: 0, showProduct: false };
    for (const section of sections) {
      for (const group of section.groups) {
        const included = group.items.filter((item) => item.include);
        stats.all += group.items.length;
        if (included.length) stats.groups += 1;
        if (group.items.some((item) => item.candidates.length)) stats.showProduct = true;
        for (const item of included) {
          stats.lines += 1;
          const warnings = warningsFor(item);
          if (warnings.includes('noPrice')) stats.noPrice += 1;
          if (warnings.includes('lowMargin')) stats.lowMargin += 1;
          if (warnings.includes('priceGap') || warnings.includes('unitCheck')) stats.check += 1;
          if (item.productId) stats.products += 1;
          if (item.isOptional) stats.optional += lineTotal(item);
          else {
            stats.total += lineTotal(item);
            stats.cost += toNumber(item.unitPurchasePrice) * toNumber(item.quantity) * (1 - toNumber(item.discountPercent) / 100);
          }
        }
      }
    }
    return stats;
  }, [sections]);

  const apply = async () => {
    const payload = sections
      .map((section) => ({
        title: section.title,
        groups: section.groups
          .map((group) => ({
            title: group.title,
            items: group.items
              .filter((item) => item.include && item.description.trim())
              .map((item) => ({
                description: item.description.trim(),
                quantity: toNumber(item.quantity) || 1,
                unitPurchasePrice: toNumber(item.unitPurchasePrice),
                unitSellingPrice: toNumber(item.unitSellingPrice),
                discountPercent: toNumber(item.discountPercent),
                isOptional: item.isOptional,
                productId: item.productId,
                details: item.details
              }))
          }))
          .filter((group) => group.items.length)
      }))
      .filter((section) => section.groups.length);
    if (!payload.length) return;
    const usedTerms = terms.filter((term) => term.use && term.value.trim());
    const termsPayload = usedTerms.length
      ? Object.fromEntries(
          usedTerms.map((term) => [
            term.key,
            term.key === 'paymentLines'
              ? term.value
                  .split('\n')
                  .map((line) => line.trim())
                  .filter(Boolean)
              : term.value.trim()
          ])
        )
      : null;
    setPhase('applying');
    setError(null);
    try {
      const response = await requestJson(`/api/project-quotations/${quotationId}/text-import/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: runMode,
          language: result?.language || language,
          sections: payload,
          introduction: introduction.use && introduction.text.trim() ? introduction.text : null,
          terms: termsPayload
        })
      });
      setOutcome(response);
      setPhase('applied');
      if (onApplied) onApplied();
    } catch (err) {
      setError(err.message);
      setPhase('review');
    }
  };

  const startOver = () => {
    runRef.current += 1;
    setText('');
    setInstructions('');
    setReplace(false);
    resetProposal();
    setPhase('input');
  };

  // After a finished import the next opening starts with an empty text, not the old success message
  useEffect(() => {
    if (open && phase === 'applied') startOver();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const textLength = text.trim().length;
  const replacing = runMode === 'replace';
  const reviewing = phase === 'review' || phase === 'applying';
  const primaryAction =
    phase === 'input'
      ? { content: 'Build quotation', icon: MagicIcon, onAction: start, disabled: textLength < MIN_TEXT || textLength > MAX_TEXT }
      : reviewing
      ? {
          content: summary.lines ? `${replacing ? 'Replace with' : 'Create'} ${summary.lines} line${summary.lines === 1 ? '' : 's'}` : 'Create lines',
          onAction: apply,
          loading: phase === 'applying',
          disabled: !summary.lines,
          destructive: replacing
        }
      : phase === 'applied'
      ? { content: 'Done', onAction: onClose }
      : undefined;
  const secondaryActions = reviewing
    ? [{ content: 'Back to the text', onAction: () => setPhase('input'), disabled: phase === 'applying' }]
    : phase === 'applied'
    ? [{ content: 'Start a new text', onAction: startOver }]
    : [{ content: 'Close', onAction: onClose }];

  const activeStep = Math.max(
    0,
    STEPS.findIndex((step) => step.id === progress.step)
  ); // 'prepare' (loading reference data) shows as the first step
  const columns = 9 + (summary.showProduct ? 1 : 0);

  return (
    <Modal open={open} onClose={onClose} title="Text to quotation" large primaryAction={primaryAction} secondaryActions={secondaryActions}>
      <style>{`${STYLES}
        .Polaris-Modal-Dialog__Modal { max-width: ${reviewing ? 1320 : 900}px !important; width: min(${reviewing ? 1320 : 900}px, 96vw) !important; }`}</style>
      <Modal.Section>
        <div className="tq-root">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          )}

          {phase === 'input' && (
            <>
              <Text as="p" tone="subdued">
                Paste a customer request, an email, meeting notes, a specification or an old quotation. The AI writes a quotation from it: sections and groups with their own titles, the lines that
                support them, the introduction and the terms the text mentions. You review everything before anything is saved.
              </Text>
              <TextField
                label="Text"
                labelHidden
                value={text}
                onChange={setText}
                multiline={14}
                maxHeight={420}
                autoComplete="off"
                placeholder={
                  'e.g.\nCall with Sophie (Torrefaction Lambert): they want an audit of their roastery, a 3-year plan for layout and automation with a budget per phase, Roasthubs for recipes and traceability on the two roasters, training for the roastmaster and two operators, and support after go-live…'
                }
                showCharacterCount
                maxLength={MAX_TEXT}
                error={textLength > 0 && textLength < MIN_TEXT ? 'Paste a bit more text' : undefined}
              />
              <div className="tq-options">
                <Select label="Write the quotation in" options={LANGUAGE_OPTIONS} value={language} onChange={setLanguage} />
                <TextField
                  label="Extra instructions (optional)"
                  value={instructions}
                  onChange={setInstructions}
                  autoComplete="off"
                  maxLength={1000}
                  placeholder="e.g. prices in the text are supplier prices · keep it to one section · address the letter to Mr Dupont"
                />
              </div>
              {existingLineCount > 0 && (
                <Checkbox
                  label={`Replace the ${existingLineCount} existing line${existingLineCount === 1 ? '' : 's'} of this version`}
                  helpText={replace ? 'The current lines and group names are removed when you create the new lines.' : 'Off: the new sections are added after the existing lines.'}
                  checked={replace}
                  onChange={setReplace}
                />
              )}
              {result && (
                <div>
                  <Button variant="plain" onClick={() => setPhase('review')}>
                    {`Back to the last proposal (${result.stats?.items || 0} lines)`}
                  </Button>
                </div>
              )}
            </>
          )}

          {phase === 'running' && (
            <div className="tq-progress">
              <div className="tq-steps">
                {STEPS.map((step, index) => {
                  const state = activeStep > index ? 'done' : activeStep === index ? 'active' : 'idle';
                  return (
                    <div key={step.id} className={`tq-step is-${state}`}>
                      <span className="tq-dot">
                        {state === 'done' ? <Icon source={CheckCircleIcon} tone="success" /> : state === 'active' ? <Spinner size="small" /> : <span className="tq-dot-idle" />}
                      </span>
                      <span>{step.label}</span>
                    </div>
                  );
                })}
              </div>
              <Text as="p" variant="bodySm" tone="subdued">
                {`${elapsed} s · usually 10–40 seconds, a long text can take longer. You can close this window; the result waits here.`}
              </Text>
            </div>
          )}

          {reviewing && result && (
            <>
              {replacing && (
                <Banner tone="warning">{`Creating these lines removes the ${existingLineCount} existing line${existingLineCount === 1 ? '' : 's'} and group names of this version.`}</Banner>
              )}
              {result.warnings?.length > 0 && (
                <Banner tone="warning">
                  {result.warnings.map((warning, index) => (
                    <p key={index}>{warning}</p>
                  ))}
                </Banner>
              )}

              {sections.length === 0 ? (
                <div className="tq-empty">
                  <Icon source={InfoIcon} tone="subdued" />
                  <Text as="p" variant="headingSm">
                    No quotation lines found in this text
                  </Text>
                  <Text as="p" tone="subdued">
                    Go back and paste a text that describes what the customer wants.
                  </Text>
                </div>
              ) : (
                <>
                  <div className="tq-summary">
                    <div className="tq-stats">
                      <span>
                        <strong>{formatToCurrency(summary.total)}</strong> selling
                      </span>
                      {summary.optional > 0 && <span>{`+ ${formatToCurrency(summary.optional)} optional`}</span>}
                      {summary.total > 0 && <span>{`margin ${Math.round(((summary.total - summary.cost) / summary.total) * 100)} %`}</span>}
                      <span>{`${summary.lines} of ${summary.all} lines · ${summary.groups} groups${summary.products ? ` · ${summary.products} catalog products` : ''}`}</span>
                      {summary.noPrice > 0 && <span className="tq-chip is-bad">{`${summary.noPrice} need a price`}</span>}
                      {summary.lowMargin > 0 && <span className="tq-chip is-warn">{`${summary.lowMargin} under the 54 % markup`}</span>}
                      {summary.check > 0 && <span className="tq-chip is-warn">{`${summary.check} price${summary.check === 1 ? '' : 's'} to check`}</span>}
                    </div>
                    <Button variant="plain" onClick={() => setIncluded(summary.lines !== summary.all)}>
                      {summary.lines === summary.all ? 'Deselect all' : 'Select all'}
                    </Button>
                  </div>

                  <div className="tq-table-wrap">
                    <table className="tq-table">
                      <thead>
                        <tr>
                          <th />
                          <th className="num">#</th>
                          <th>Description</th>
                          {summary.showProduct && <th>Product</th>}
                          <th className="num">Qty</th>
                          <th className="num">AP</th>
                          <th className="num">VP</th>
                          <th className="num">Total</th>
                          <th>Price from</th>
                          <th>Opt.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sections.map((section) => {
                          const sectionItems = section.groups.flatMap((group) => group.items);
                          const sectionIncluded = sectionItems.filter((item) => item.include);
                          const sectionTotal = sectionIncluded.filter((item) => !item.isOptional).reduce((sum, item) => sum + lineTotal(item), 0);
                          return [
                            <tr key={section.key} className="tq-section">
                              <td>
                                <Checkbox
                                  label="Include this section"
                                  labelHidden
                                  checked={sectionIncluded.length === sectionItems.length ? true : sectionIncluded.length ? 'indeterminate' : false}
                                  onChange={() => setIncluded(sectionIncluded.length !== sectionItems.length, section.key)}
                                />
                              </td>
                              <td colSpan={columns - 4}>
                                <div className="tq-head">
                                  <input
                                    className="tq-input tq-section-input"
                                    value={section.title}
                                    maxLength={200}
                                    disabled={section.merged}
                                    onChange={(event) => updateSection(section.key, (current) => ({ ...current, title: event.target.value }))}
                                    aria-label="Section title"
                                  />
                                  {section.merged && <span className="tq-moved">joins the last section of this version</span>}
                                </div>
                              </td>
                              <td className="num tq-total" style={{ fontWeight: 700, paddingTop: 11 }}>
                                {formatToCurrency(sectionTotal)}
                              </td>
                              <td colSpan={2} />
                            </tr>,
                            ...section.groups.flatMap((group) => {
                              const included = group.items.filter((item) => item.include);
                              const subtotal = included.filter((item) => !item.isOptional).reduce((sum, item) => sum + lineTotal(item), 0);
                              return [
                                <tr key={group.key} className="tq-group">
                                  <td>
                                    <Checkbox
                                      label="Include this group"
                                      labelHidden
                                      checked={included.length === group.items.length ? true : included.length ? 'indeterminate' : false}
                                      onChange={() => setIncluded(included.length !== group.items.length, section.key, group.key)}
                                    />
                                  </td>
                                  <td className="tq-code num" style={{ paddingTop: 10 }}>
                                    {group.lineNumber}
                                  </td>
                                  <td colSpan={columns - 5}>
                                    <input
                                      className="tq-input tq-label-input"
                                      value={group.title}
                                      maxLength={200}
                                      onChange={(event) => updateGroup(section.key, group.key, (current) => ({ ...current, title: event.target.value }))}
                                      aria-label="Group title"
                                    />
                                  </td>
                                  <td className="num tq-total" style={{ fontWeight: 600, paddingTop: 10 }}>
                                    {formatToCurrency(subtotal)}
                                  </td>
                                  <td colSpan={2} />
                                </tr>,
                                ...group.items.map((item) => (
                                  <ItemRow
                                    key={item.id}
                                    item={item}
                                    showProduct={summary.showProduct}
                                    onChange={(patch) => updateItem(section.key, group.key, item.id, patch)}
                                    onToggle={() => updateItem(section.key, group.key, item.id, { include: !item.include })}
                                  />
                                ))
                              ];
                            })
                          ];
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="tq-cards">
                    <div className={`tq-card${introduction.use ? '' : ' is-off'}`}>
                      <div className="tq-card-head">
                        <Checkbox
                          label={hasIntroduction && !replacing ? 'Replace the PDF introduction' : 'Use as PDF introduction'}
                          checked={introduction.use}
                          disabled={!introduction.text.trim()}
                          onChange={(value) => setIntroduction((current) => ({ ...current, use: value }))}
                        />
                        {hasIntroduction && !replacing && !introduction.use && <span className="tq-hint">This version already has an introduction</span>}
                      </div>
                      {introduction.text ? (
                        <textarea
                          className="tq-textarea"
                          rows={10}
                          value={introduction.text}
                          disabled={!introduction.use}
                          onChange={(event) => setIntroduction((current) => ({ ...current, text: event.target.value }))}
                          aria-label="Introduction"
                        />
                      ) : (
                        <span className="tq-hint">No introduction was written. Use “Write with AI” in the PDF tab.</span>
                      )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
                      <div className="tq-card">
                        <Text as="span" variant="headingSm">
                          Terms from the text
                        </Text>
                        {terms.length ? (
                          terms.map((term) => (
                            <div key={term.key} className="tq-term">
                              <Checkbox
                                label={`Fill in ${term.label}`}
                                labelHidden
                                checked={term.use}
                                onChange={(value) => setTerms((current) => current.map((entry) => (entry.key === term.key ? { ...entry, use: value } : entry)))}
                              />
                              <span className="tq-term-label">{term.label}</span>
                              {term.key === 'paymentLines' ? (
                                <textarea
                                  className="tq-input is-boxed"
                                  rows={Math.max(2, term.value.split('\n').length)}
                                  value={term.value}
                                  disabled={!term.use}
                                  onChange={(event) => setTerms((current) => current.map((entry) => (entry.key === term.key ? { ...entry, value: event.target.value } : entry)))}
                                  aria-label={term.label}
                                />
                              ) : (
                                <input
                                  className="tq-input is-boxed"
                                  value={term.value}
                                  disabled={!term.use}
                                  onChange={(event) => setTerms((current) => current.map((entry) => (entry.key === term.key ? { ...entry, value: event.target.value } : entry)))}
                                  aria-label={term.label}
                                />
                              )}
                            </div>
                          ))
                        ) : (
                          <span className="tq-hint">The text states no delivery time, warranty, validity or payment terms; the current terms stay as they are.</span>
                        )}
                      </div>

                      {result.notes?.length > 0 && (
                        <div className="tq-notes">
                          <Text as="span" variant="headingSm">
                            Also in the text
                          </Text>
                          <ul>
                            {result.notes.map((note, index) => (
                              <li key={index}>{note}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  <Text as="p" variant="bodySm" tone="subdued">
                    {`Written in ${
                      LANGUAGE_OPTIONS.find((option) => option.value === result.language)?.label || 'English'
                    }. Technical details go into the technical description of each line. Built in ${Math.round((result.ms || 0) / 1000)} s.`}
                  </Text>
                </>
              )}
            </>
          )}

          {phase === 'applied' && outcome && (
            <div className="tq-empty">
              <Icon source={CheckCircleIcon} tone="success" />
              <Text as="p" variant="headingMd">
                {`${outcome.created} line${outcome.created === 1 ? '' : 's'} created in ${outcome.groups} group${outcome.groups === 1 ? '' : 's'}`}
              </Text>
              <Text as="p" tone="subdued">
                {[
                  outcome.replaced ? 'The previous lines were replaced.' : null,
                  outcome.introduction ? 'The PDF introduction is filled in.' : null,
                  outcome.terms ? 'The terms from the text are filled in.' : null,
                  'Check the pricing table and the PDF tab.'
                ]
                  .filter(Boolean)
                  .join(' ')}
              </Text>
              {summary.noPrice > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon source={AlertTriangleIcon} tone="warning" />
                  <Text as="span" tone="caution">{`${summary.noPrice} line${summary.noPrice === 1 ? ' still needs' : 's still need'} a price.`}</Text>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal.Section>
    </Modal>
  );
}
